import { createEntityAdapter, createSelector, createSlice, EntityState, PayloadAction } from '@reduxjs/toolkit'
import { MeetingTemplate } from '../ai/meetingTemplate'
import { RootState } from '../store'
import { RoomSession } from '../../models/Room'

interface MeetingTemplatesState {
  isLoading: boolean
  meetingTemplates: EntityState<MeetingTemplate, string>
  selectedMeetingTemplate: MeetingTemplate | null
  defaultMeetingTemplate: MeetingTemplate | null
}

const meetingTemplatesAdapter = createEntityAdapter<MeetingTemplate>()

const initialState: MeetingTemplatesState = {
  isLoading: true,
  meetingTemplates: meetingTemplatesAdapter.getInitialState(),
  selectedMeetingTemplate: null,
  defaultMeetingTemplate: null,
}

export const meetingTemplatesSlice = createSlice({
  name: 'meetingTemplates',
  initialState,
  reducers: {
    setMeetingTemplatesLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    setMeetingTemplates: (state, action: PayloadAction<MeetingTemplate[]>) => {
      meetingTemplatesAdapter.setAll(state.meetingTemplates, action.payload)
    },
    setSelectedMeetingTemplate: (state, action: PayloadAction<string>) => {
      const selectedMeetingTemplateId = action.payload
      const selectedMeetingTemplate = meetingTemplatesAdapter
        .getSelectors((state: MeetingTemplatesState) => state.meetingTemplates)
        .selectById(state, selectedMeetingTemplateId)
      if (selectedMeetingTemplate) {
        state.selectedMeetingTemplate = selectedMeetingTemplate
      }
    },
    setDefaultMeetingTemplate: (state, action: PayloadAction<MeetingTemplate>) => {
      const defaultMeetingTemplate = action.payload
      if (defaultMeetingTemplate) {
        state.defaultMeetingTemplate = defaultMeetingTemplate
      }
    },
    resetMeetingTemplates: () => initialState,
  },
})

export const { setMeetingTemplatesLoading, setMeetingTemplates, setSelectedMeetingTemplate, setDefaultMeetingTemplate, resetMeetingTemplates } =
  meetingTemplatesSlice.actions

const meetingTemplateSelectors = meetingTemplatesAdapter.getSelectors((state: RootState) => state.meetingSummary.meetingTemplates.meetingTemplates)

export const selectSelectedMeetingTemplate = (state: RootState) => state.meetingSummary.meetingTemplates.selectedMeetingTemplate

export const selectSessionMeetingTemplate = createSelector(
  [
    (state: RootState) => state.meetingSummary.roomSession.roomSession,
    (state: RootState) => meetingTemplateSelectors.selectAll(state),
    (state: RootState) => state.meetingSummary.meetingTemplates.defaultMeetingTemplate,
  ],
  (roomSession, meetingTemplates, defaultMeetingTemplate) => {
    return getSessionMeetingTemplate(roomSession, meetingTemplates, defaultMeetingTemplate)
  },
)

export const selectMeetingTemplates = (state: RootState) => meetingTemplateSelectors.selectAll(state)

const getSessionMeetingTemplate = (roomSession: RoomSession | null, meetingTemplates: MeetingTemplate[], defaultMeetingTemplate: MeetingTemplate | null) => {
  const sessionMetadata = getRoomSessionMetadata(roomSession)
  const sessionMeetingTemplateId = sessionMetadata.external?.meetingTemplateId
  const sessionMeetingTemplate = sessionMeetingTemplateId ? meetingTemplates.find((meetingTemplate) => meetingTemplate.id === sessionMeetingTemplateId) : null
  return sessionMeetingTemplate ?? defaultMeetingTemplate ?? null
}

const getRoomSessionMetadata = (roomSession: RoomSession | null) => {
  try {
    return roomSession ? JSON.parse(roomSession.metadata) : {}
  } catch (e) {
    return {}
  }
}
